import { useClinicalRecordLock } from '../../../../../components/clinical-record-lock';
import {
  Card,
  Box,
  Typography,
  Stack,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import RxOutcome from '../CLFitting/RxOutcome';
import { LoadingButton } from '@mui/lab';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import FormProvider from '../../../../../components/hook-form/FormProvider';
import { Controller, useForm } from 'react-hook-form';
import RHFTextField from '../../../../../components/hook-form/RHFTextField';
import { ICLHandover } from '../../../../../@types/clHandover.type';
import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { CLHandoverService } from '../../../../../api/contact-lenses/cl-handover.service';
import { useParams, useNavigate } from 'react-router-dom';
import { PATH_DASHBOARD } from '../../../../../routes/paths';
import { enqueueSnackbar } from '../../../../../components/snackbar';
import {
  getApiErrorMessage,
  notifyHandoverSaveResult,
} from '../../../../../utils/util';
import { IClinicalRecordSchema } from '../../../../../@types/clinicalRecord.type';
import SignatureDialog from '../../../../../components/signature/SignatureDialog';
import SignedOffBanner from '../../../../../components/signed-off-banner';
import { ClinicalRecords } from '../../../../../api/clinicalRecords.service';
import { DispenseService } from '../../../../../api/dispense.service';
import FinalSummary from '../../Refraction/FinalSummary';
import { SupplierService } from '../../../../../api/supplier.service';
import { ProductService } from '../../../../../api/product.service';
import { ProductType } from '../../../../../@types/product';

function LinkedDispenseTraceability({
  clinicalRecordId,
}: {
  clinicalRecordId: number;
}) {
  const [rows, setRows] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoaded(false);
    (async () => {
      try {
        const res = await new DispenseService().getDispensesByClinicalRecord(
          clinicalRecordId,
        );
        if (!cancelled) {
          setRows(Array.isArray(res.data) ? res.data : []);
        }
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clinicalRecordId]);

  const flatRows = rows.flatMap((d) =>
    (d.productLines?.length ? d.productLines : []).map((line: any) => ({
      dispenseId: d.id,
      line,
    })),
  );

  // Temporarily disabled helper text while traceability panel is hidden.
  // const helperText =
  //   rows.length > 0
  //     ? "A dispense is linked to this visit, but no product lines are recorded yet. Add products (and lots where required) on the Dispense screen for this patient; saved lines will appear here."
  //     : "When a dispense is linked to this clinical record and product lines are saved on Dispense, batch and lot details will appear here for traceability.";

  if (!loaded) return null;

  return (
    <Box sx={{ py: 2, px: 1, borderTop: '1px solid', borderColor: 'divider' }}>
      <Typography variant="subtitle1" sx={{ mb: 1 }}>
        Linked dispenses & issued lots
      </Typography>
      {flatRows.length > 0 ? (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Dispense</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Qty</TableCell>
              <TableCell>Batch / lot</TableCell>
              <TableCell>Expiry</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {flatRows.map(({ dispenseId, line }) => (
              <TableRow key={`${dispenseId}-${line.id}`}>
                <TableCell>{dispenseId}</TableCell>
                <TableCell>{line.product?.name ?? line.productId}</TableCell>
                <TableCell>{line.quantity}</TableCell>
                <TableCell>
                  {line.batchNumber ?? line.goodsInId ?? '—'}
                </TableCell>
                <TableCell>
                  {line.expiryDate
                    ? new Date(line.expiryDate).toLocaleDateString()
                    : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : null}
    </Box>
  );
}

type Props = {
  clinicalRecordId: number;
  tab: (type: string) => void;
  clHandover: ICLHandover;
  showCLForm: boolean;
  getCLHandoverRecords: (clHandoverId: number, clinicalId: number) => void;
  clinicalRecord?: IClinicalRecordSchema | any;
  onUpdate?: () => void;
};

const CLHandover = ({
  clinicalRecordId,
  tab,
  clHandover,
  showCLForm,
  getCLHandoverRecords,
  clinicalRecord,
  onUpdate,
  finalData,
}: Props & { finalData?: any }) => {
  const lock = useClinicalRecordLock();
  const [visitType, setVisitType] = useState(
    clHandover?.visitType ? clHandover?.visitType : '',
  );
  const [isSignatureDialogOpen, setIsSignatureDialogOpen] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isSavingHandover, setIsSavingHandover] = useState(false);
  const [pendingData, setPendingData] = useState<ICLHandover | null>(null);
  const [supplierOptions, setSupplierOptions] = useState<string[]>([]);
  const [typeOptions, setTypeOptions] = useState<string[]>([]);
  const [freqOptions, setFreqOptions] = useState<string[]>([]);
  const [lensOptions, setLensOptions] = useState<string[]>([]);
  const [solutionOptions, setSolutionOptions] = useState<string[]>([]);
  const [supplierInput, setSupplierInput] = useState('');
  const [typeInput, setTypeInput] = useState('');
  const [freqInput, setFreqInput] = useState('');
  const [lensInput, setLensInput] = useState('');
  const [solutionInput, setSolutionInput] = useState('');
  const { patientId } = useParams();
  const navigate = useNavigate();

  const [debouncedSupplierInput, setDebouncedSupplierInput] = useState('');
  const [debouncedTypeInput, setDebouncedTypeInput] = useState('');
  const [debouncedFreqInput, setDebouncedFreqInput] = useState('');
  const [debouncedLensInput, setDebouncedLensInput] = useState('');
  const [debouncedSolutionInput, setDebouncedSolutionInput] = useState('');

  const exitToPatientScreen = () => {
    navigate(PATH_DASHBOARD.patients.list);
  };

  const hasClinicalData = !!(
    clinicalRecord?.clHistoryAndSymptomsId ||
    clinicalRecord?.anteriorEyeId ||
    clinicalRecord?.contactLensFittingId ||
    clinicalRecord?.contactLensAfterCareId
  );
  const todayStart = dayjs().startOf('day');
  const examDateStart = dayjs(clinicalRecord?.createdDate).isValid()
    ? dayjs(clinicalRecord?.createdDate).startOf('day')
    : null;
  const minAllowedDate = (
    examDateStart && examDateStart.isAfter(todayStart)
      ? examDateStart
      : todayStart
  ).toDate();

  const performHandover = async (data: ICLHandover) => {
    if (isSavingHandover) return;
    setIsSavingHandover(true);
    const quantityIssuedCandidate = Number(data.quantityIssued);
    const parsedQuantityIssued = Number.isFinite(quantityIssuedCandidate)
      ? quantityIssuedCandidate
      : undefined;
    const packSizeCandidate = Number(data.packSize);
    const parsedPackSizeRaw = Number.isFinite(packSizeCandidate)
      ? packSizeCandidate
      : undefined;
    const handoverData = { ...data, visitType, clinicalRecordId };
    const formattedData = {
      ...handoverData,
      quantityIssued:
        parsedQuantityIssued == null || Number.isNaN(parsedQuantityIssued)
          ? undefined
          : parsedQuantityIssued,
      packSize:
        parsedPackSizeRaw == null || Number.isNaN(parsedPackSizeRaw)
          ? undefined
          : Math.max(1, Math.floor(parsedPackSizeRaw)),
      recallDate: dayjs(data.recallDate).isValid()
        ? dayjs(data.recallDate).format('YYYY-MM-DD')
        : new Date(),
      systemRecallDate: dayjs(data.systemRecallDate).isValid()
        ? dayjs(data.systemRecallDate).format('YYYY-MM-DD')
        : new Date(),
    };
    // Amending a signed record: stage the change instead of saving it.
    if (
      lock.stageIfAmending(
        'contactLensHandover',
        formattedData as unknown as Record<string, unknown>,
      )
    ) {
      setIsSavingHandover(false);
      return;
    }

    try {
      const clHandoverService = new CLHandoverService();
      const response = await clHandoverService.createCLHandover(
        patientId!,
        formattedData,
      );
      notifyHandoverSaveResult(
        enqueueSnackbar,
        'CL Handover Created Successfully!',
        response.data?.recallWarning,
      );
      getCLHandoverRecords(response.data?.id, clinicalRecordId);
    } catch (error: unknown) {
      enqueueSnackbar(getApiErrorMessage(error, 'Failed to save CL handover'), {
        variant: 'error',
      });
    } finally {
      setIsSavingHandover(false);
    }
  };

  const handleSignOff = async (signatureData: string) => {
    if (!clinicalRecordId) return;
    setIsSigning(true);
    try {
      const service = new ClinicalRecords();
      await service.signOff(clinicalRecordId, signatureData);
      enqueueSnackbar('Clinical record signed off successfully', {
        variant: 'success',
      });
      setIsSignatureDialogOpen(false);

      // PROCEED WITH HANDOVER AFTER SIGN-OFF
      if (pendingData) {
        await performHandover(pendingData);
        setPendingData(null);
      } else {
        onUpdate && onUpdate();
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar('Failed to sign off clinical record', {
        variant: 'error',
      });
    } finally {
      setIsSigning(false);
    }
  };

  const methods = useForm<ICLHandover>();

  const { handleSubmit, control, reset } = methods;

  const getTopMatches = (options: string[], query: string): string[] => {
    const q = (query ?? '').trim().toLowerCase();
    if (!q) return options.slice(0, 5);
    return options.filter((v) => v.toLowerCase().includes(q)).slice(0, 5);
  };

  const handleChange = (e: any) => {
    setVisitType(e.target.value);
  };

  const onSubmit = async (data: ICLHandover) => {
    const recallDate = dayjs(data.recallDate);
    const systemRecallDate = dayjs(data.systemRecallDate);
    const minDay = dayjs(minAllowedDate);

    if (recallDate.isValid() && recallDate.isBefore(minDay, 'day')) {
      enqueueSnackbar('Recall Date cannot be before the examination date.', {
        variant: 'error',
      });
      return;
    }

    if (
      systemRecallDate.isValid() &&
      systemRecallDate.isBefore(minDay, 'day')
    ) {
      enqueueSnackbar(
        'System Recall Date cannot be before the examination date.',
        { variant: 'error' },
      );
      return;
    }

    if (isSigning || isSavingHandover) return;
    if (hasClinicalData && !clinicalRecord?.signedAt) {
      setPendingData(data);
      setIsSignatureDialogOpen(true);
    } else {
      await performHandover(data);
    }
  };

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSupplierInput(supplierInput), 250);
    return () => clearTimeout(t);
  }, [supplierInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedTypeInput(typeInput), 250);
    return () => clearTimeout(t);
  }, [typeInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedFreqInput(freqInput), 250);
    return () => clearTimeout(t);
  }, [freqInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedLensInput(lensInput), 250);
    return () => clearTimeout(t);
  }, [lensInput]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSolutionInput(solutionInput), 250);
    return () => clearTimeout(t);
  }, [solutionInput]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [suppliersRes, productsRes] = await Promise.all([
          new SupplierService().getAllSuppliers(),
          new ProductService().getActiveProducts(),
        ]);
        if (cancelled) return;
        const suppliers = Array.isArray(suppliersRes.data)
          ? suppliersRes.data
          : [];
        const products = Array.isArray(productsRes.data)
          ? productsRes.data
          : [];
        const supplierNames = suppliers
          .map((s: any) => String(s?.name ?? '').trim())
          .filter(Boolean);
        const clProducts = products.filter(
          (p: any) => p?.type === ProductType.CONTACT_LENSES,
        );
        const solutionProducts = products.filter(
          (p: any) => p?.type === ProductType.SOLUTION,
        );
        const clCategories = clProducts
          .map((p: any) => String(p?.category ?? '').trim())
          .filter(Boolean);
        const clModalities = clProducts
          .map((p: any) => String(p?.productDetails?.modality ?? '').trim())
          .filter(Boolean);
        const clNames = clProducts
          .map((p: any) => String(p?.name ?? '').trim())
          .filter(Boolean);
        const solNames = solutionProducts
          .map((p: any) => String(p?.name ?? '').trim())
          .filter(Boolean);
        setSupplierOptions(Array.from(new Set(supplierNames)));
        setTypeOptions(Array.from(new Set(clCategories)));
        setFreqOptions(Array.from(new Set(clModalities)));
        setLensOptions(Array.from(new Set(clNames)));
        setSolutionOptions(Array.from(new Set(solNames)));
      } catch {
        if (cancelled) return;
        setSupplierOptions([]);
        setTypeOptions([]);
        setFreqOptions([]);
        setLensOptions([]);
        setSolutionOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (showCLForm) {
      reset({
        supplier: '',
        type: '',
        freq: '',
        lens: '',
        solution: '',
        packSize: 1,
        quantityIssued: 1,
        visitType: '',
        recallDate: new Date(),
        systemRecallDate: new Date(),
        recallReason: '',
        patientAdvise: '',
        orderLenses: '',
        handoverNotes: '',
      });
      setSupplierInput('');
      setTypeInput('');
      setFreqInput('');
      setLensInput('');
      setSolutionInput('');
    } else {
      const supplier = clHandover?.supplier || '';
      const type = clHandover?.type || '';
      const freq = clHandover?.freq || '';
      const lens = clHandover?.lens || '';
      const solution = clHandover?.solution || '';
      reset({
        supplier,
        type,
        freq,
        lens,
        solution,
        packSize: clHandover?.packSize ?? 1,
        quantityIssued: clHandover?.quantityIssued ?? 1,
        visitType: clHandover?.visitType || '',
        recallDate: dayjs(clHandover?.recallDate).isValid()
          ? dayjs(clHandover?.recallDate).toDate()
          : new Date(),
        systemRecallDate: dayjs(clHandover?.systemRecallDate).isValid()
          ? dayjs(clHandover?.systemRecallDate).toDate()
          : new Date(),
        recallReason: clHandover?.recallReason || '',
        patientAdvise: clHandover?.patientAdvise || '',
        orderLenses: clHandover?.orderLenses || '',
        handoverNotes: clHandover?.handoverNotes || '',
      });
      setSupplierInput(supplier);
      setTypeInput(type);
      setFreqInput(freq);
      setLensInput(lens);
      setSolutionInput(solution);
    }
  }, [showCLForm, clHandover, reset]);

  useEffect(() => {
    if (clHandover) {
      setVisitType(clHandover?.visitType || '');
    }
  }, [clHandover, setVisitType]);

  return (
    <Card sx={{ p: 3 }}>
      {!hasClinicalData && (
        <Box sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="h6" color="warning.main" gutterBottom>
            No clinical data found.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please complete at least one clinical form before signing off.
          </Typography>
          <Stack
            direction="row"
            spacing={2}
            justifyContent="center"
            flexWrap="wrap"
          >
            <LoadingButton variant="contained" disabled>
              Sign Off Clinical Record
            </LoadingButton>
            <LoadingButton variant="outlined" disabled>
              Save And Next
            </LoadingButton>
            <LoadingButton variant="outlined" onClick={exitToPatientScreen}>
              Exit
            </LoadingButton>
          </Stack>
        </Box>
      )}

      {hasClinicalData &&
        (!clinicalRecord?.signedAt ||
          !['Contact Lenses', 'Contact Lens'].includes(
            clinicalRecord?.type,
          )) && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="h6" color="info.main" gutterBottom>
              Clinical record must be signed off before handover.
            </Typography>
            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              <LoadingButton
                variant="contained"
                color="primary"
                onClick={() => setIsSignatureDialogOpen(true)}
                loading={isSigning}
                disabled={isSigning || isSavingHandover}
              >
                Sign Off Clinical Record
              </LoadingButton>
              <LoadingButton
                variant="outlined"
                onClick={exitToPatientScreen}
                disabled={isSigning || isSavingHandover}
              >
                Exit
              </LoadingButton>
            </Stack>
          </Box>
        )}

      {hasClinicalData &&
        clinicalRecord?.signedAt &&
        clinicalRecord?.type === 'Contact Lenses' && (
          <SignedOffBanner
            signedBy={clinicalRecord.signedByUser?.username}
            signedAt={clinicalRecord.signedAt}
            sx={{ mx: 2, mb: 2 }}
          />
        )}

      {hasClinicalData &&
        clinicalRecord?.signedAt &&
        clinicalRecord?.type === 'Contact Lenses' && (
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ px: 2, pt: 1 }}>
              <FinalSummary finalData={finalData} />
              {/* Temporarily hidden per request:
            <LinkedDispenseTraceability clinicalRecordId={clinicalRecordId} />
            */}
            </Box>
            {(showCLForm ||
              !clHandover ||
              Object.keys(clHandover).length === 0) && (
              <Typography sx={{ ml: 3, mt: 2, mb: 1 }} color={'red'}>
                * There's no CL Handover added for this clinical record, please
                add and save!
              </Typography>
            )}
            <Box
              sx={{
                margin: '2% 10%',
              }}
            >
              {/* <RxOutcome /> */}
              <Box sx={{ mx: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Supplier
                  </Typography>
                  <Box sx={{ width: '33.33%' }}>
                    <Controller
                      name="supplier"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          freeSolo
                          sx={{ width: '100%' }}
                          options={getTopMatches(
                            supplierOptions,
                            debouncedSupplierInput,
                          )}
                          value={field.value ?? ''}
                          inputValue={supplierInput}
                          onInputChange={(_, value) => {
                            setSupplierInput(value);
                            field.onChange(value ?? '');
                          }}
                          onChange={(_, value) => field.onChange(value ?? '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label="Supplier"
                            />
                          )}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Type
                  </Typography>
                  <Box sx={{ width: '33.33%' }}>
                    <Controller
                      name="type"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          freeSolo
                          sx={{ width: '100%' }}
                          options={getTopMatches(
                            typeOptions,
                            debouncedTypeInput,
                          )}
                          value={field.value ?? ''}
                          inputValue={typeInput}
                          onInputChange={(_, value) => {
                            setTypeInput(value);
                            field.onChange(value ?? '');
                          }}
                          onChange={(_, value) => field.onChange(value ?? '')}
                          renderInput={(params) => (
                            <TextField {...params} size="small" label="Type" />
                          )}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Freq
                  </Typography>
                  <Box sx={{ width: '33.33%' }}>
                    <Controller
                      name="freq"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          freeSolo
                          sx={{ width: '100%' }}
                          options={getTopMatches(
                            freqOptions,
                            debouncedFreqInput,
                          )}
                          value={field.value ?? ''}
                          inputValue={freqInput}
                          onInputChange={(_, value) => {
                            setFreqInput(value);
                            field.onChange(value ?? '');
                          }}
                          onChange={(_, value) => field.onChange(value ?? '')}
                          renderInput={(params) => (
                            <TextField {...params} size="small" label="Freq" />
                          )}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Lens
                  </Typography>
                  <Box sx={{ width: '33.33%' }}>
                    <Controller
                      name="lens"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          freeSolo
                          sx={{ width: '100%' }}
                          options={getTopMatches(
                            lensOptions,
                            debouncedLensInput,
                          )}
                          value={field.value ?? ''}
                          inputValue={lensInput}
                          onInputChange={(_, value) => {
                            setLensInput(value);
                            field.onChange(value ?? '');
                          }}
                          onChange={(_, value) => field.onChange(value ?? '')}
                          renderInput={(params) => (
                            <TextField {...params} size="small" label="Lens" />
                          )}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Solution
                  </Typography>
                  <Box sx={{ width: '33.33%' }}>
                    <Controller
                      name="solution"
                      control={control}
                      render={({ field }) => (
                        <Autocomplete
                          freeSolo
                          sx={{ width: '100%' }}
                          options={getTopMatches(
                            solutionOptions,
                            debouncedSolutionInput,
                          )}
                          value={field.value ?? ''}
                          inputValue={solutionInput}
                          onInputChange={(_, value) => {
                            setSolutionInput(value);
                            field.onChange(value ?? '');
                          }}
                          onChange={(_, value) => field.onChange(value ?? '')}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              label="Solution"
                            />
                          )}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Pack Size
                  </Typography>
                  <RHFTextField
                    name="packSize"
                    type="number"
                    size="small"
                    sx={{ width: '33.33%' }}
                    inputProps={{ min: 1, step: 1 }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Quantity Issued
                  </Typography>
                  <RHFTextField
                    name="quantityIssued"
                    type="number"
                    size="small"
                    sx={{ width: '33.33%' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Visit Type *
                  </Typography>
                  <FormControl sx={{ width: '33.33%' }} required>
                    <InputLabel id="cl-visit-type-label">Visit Type</InputLabel>
                    <Select
                      labelId="cl-visit-type-label"
                      id="cl-visit-type"
                      label="Visit Type"
                      value={visitType}
                      onChange={handleChange}
                    >
                      <MenuItem value="New CL RX">New CL RX</MenuItem>
                      <MenuItem value="Update CL RX">Update CL RX</MenuItem>
                      <MenuItem value="Aftercare">Aftercare</MenuItem>
                      <MenuItem value="Replacement">Replacement</MenuItem>
                      <MenuItem value="Trails Evaluation">
                        Trails Evaluation
                      </MenuItem>
                      <MenuItem value="Emergency">Emergency</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Recall Date *
                  </Typography>
                  <Box sx={{ width: '33.33%' }}>
                    <Controller
                      name="recallDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          onChange={(date) => field.onChange(date)}
                          label="Recall Date *"
                          minDate={minAllowedDate}
                          sx={{ width: '100%' }}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    System Recall Date *
                  </Typography>
                  <Box sx={{ width: '33.33%' }}>
                    <Controller
                      name="systemRecallDate"
                      control={control}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          sx={{ width: '100%' }}
                          onChange={(date) => field.onChange(date)}
                          label="System Recall Date *"
                          minDate={minAllowedDate}
                        />
                      )}
                    />
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Recall Reason
                  </Typography>
                  <RHFTextField
                    name="recallReason"
                    size="small"
                    sx={{ width: '33.33%' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Patient Advise
                  </Typography>
                  <RHFTextField
                    name="patientAdvise"
                    size="small"
                    sx={{ width: '33.33%' }}
                  />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: '10px' }}>
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right' }}
                  >
                    Order Lenses
                  </Typography>
                  <RHFTextField
                    name="orderLenses"
                    size="small"
                    sx={{ width: '33.33%' }}
                  />
                </Box>
                <Box
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: '10px' }}
                >
                  <Typography
                    color={'#637381'}
                    variant="h6"
                    sx={{ width: '33.33%', pr: 2, textAlign: 'right', pt: 1.5 }}
                  >
                    Handover Notes
                  </Typography>
                  <RHFTextField
                    name="handoverNotes"
                    size="small"
                    multiline
                    rows={2}
                    sx={{ width: '33.33%' }}
                  />
                </Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row-reverse',
                  alignItems: 'center',
                  gap: 2,
                  m: 3,
                }}
              >
                <LoadingButton
                  variant="contained"
                  type="submit"
                  loading={isSavingHandover}
                  disabled={isSavingHandover || isSigning}
                >
                  Save And Next
                </LoadingButton>
                <LoadingButton
                  type="button"
                  variant="outlined"
                  onClick={exitToPatientScreen}
                  disabled={isSavingHandover || isSigning}
                >
                  Exit
                </LoadingButton>
              </Box>
            </Box>
          </FormProvider>
        )}
      <SignatureDialog
        open={isSignatureDialogOpen}
        onClose={() => setIsSignatureDialogOpen(false)}
        onConfirm={handleSignOff}
        loading={isSigning}
      />
    </Card>
  );
};

export default CLHandover;
